import { z } from 'zod';
import { zApiVersion, zId } from './manifest/common.js';
/**
 * The UI tab (docs/ui-tab.md, prd-deltas §32): marking up a running application.
 *
 * Two things live here, so the API, the web app and every capture — the Chrome
 * extension now, the iOS app later (docs/ios-prd.md §9) — share one definition:
 *
 * - `civil/ui.yaml`, the links to where each frontend runs, shared by everyone who
 *   works on the application. A civil document like any other: written by ops.
 * - The request: snapshots of what was on screen, and the marks on them. Civil's own
 *   state, like `runs` — never written to the repository.
 */
export const UI_PATH = 'civil/ui.yaml';
/** A link's name — `staging`, `production`: a plain word, so a key never says more than one thing. */
export const zLinkName = z
    .string()
    .regex(/^[a-z][a-z0-9_-]{0,31}$/, 'must be a short lowercase word: letters, digits, hyphens, underscores');
/** Where a frontend runs. Only http(s): a link is opened, never fetched by Civil. */
export const zLinkUrl = z
    .string()
    .max(2048)
    .url()
    .refine((u) => /^https?:\/\//i.test(u), { message: 'must be an http or https address' });
export const zUiFrontend = z.object({
    links: z.record(zLinkName, zLinkUrl).default({}),
});
export const zUi = z.object({
    apiVersion: zApiVersion,
    kind: z.literal('UI'),
    metadata: z.object({ name: z.string().min(1).optional() }).default({}),
    spec: z.object({
        /** Keyed by a client node's id in app.yaml. */
        frontends: z.record(zId, zUiFrontend).default({}),
    }),
});
/** The ops that write civil/ui.yaml (CLAUDE.md: every mutation is a structured op). */
export const zUiOp = z.discriminatedUnion('op', [
    z.object({ op: z.literal('setLink'), frontend: zId, name: zLinkName, url: zLinkUrl }),
    z.object({ op: z.literal('removeLink'), frontend: zId, name: zLinkName }),
]);
// ---------------------------------------------------------------------------------
// Requests, snapshots, marks.
// ---------------------------------------------------------------------------------
/** Caps, so one request cannot grow without bound. Generous for a person, small for a script. */
export const UI_LIMITS = {
    noteChars: 4000,
    titleChars: 200,
    marksPerSnapshot: 200,
    snapshotsPerRequest: 60,
    strokePoints: 2000,
    elements: 3000,
    elementText: 300,
    /** The decoded image. A phone's full-resolution screenshot fits; a video frame dump does not. */
    imageBytes: 8 * 1024 * 1024,
    imagePixels: 40_000_000,
};
const zCoord = z.number().finite().min(-100_000).max(100_000);
export const zPixel = z.object({ x: zCoord, y: zCoord });
export const zBox = z.object({ x: zCoord, y: zCoord, w: z.number().finite().min(0).max(100_000), h: z.number().finite().min(0).max(100_000) });
/** The drawing tools. A box and an arrow are two points; a pen is many; text is one, with its label. */
export const DRAW_TOOLS = ['pen', 'arrow', 'box', 'text'];
/**
 * Where a mark is, in the snapshot image's own pixels — never screen pixels, so a mark
 * is in the same place on any device that shows the snapshot.
 */
export const zMarkGeometry = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('pin'), at: zPixel }),
    z.object({ kind: z.literal('region'), box: zBox }),
    z.object({
        kind: z.literal('stroke'),
        tool: z.enum(DRAW_TOOLS),
        points: z.array(zPixel).min(1).max(UI_LIMITS.strokePoints),
        /** What a text stroke says on the drawing. The mark's note is what it means. */
        label: z.string().max(200).optional(),
    }),
]);
/**
 * One visible element, as a capture that can read the page records it. Everything in
 * it came from the page, so it is material, never instruction (prd-deltas §28) — and
 * `components` is a hint the page could have lied about (docs/ui-tab.md §6).
 */
export const zElement = z.object({
    box: zBox,
    tag: z.string().max(40),
    text: z.string().max(UI_LIMITS.elementText).optional(),
    role: z.string().max(60).optional(),
    name: z.string().max(UI_LIMITS.elementText).optional(),
    selector: z.string().max(500).optional(),
    components: z.array(z.string().max(120)).max(20).optional(),
});
export const CAPTURED_BY = ['upload', 'extension', 'ios'];
export const zViewport = z.object({
    width: z.number().int().min(1).max(20_000),
    height: z.number().int().min(1).max(20_000),
    scale: z.number().finite().min(0.1).max(10).default(1),
});
/** What a capture sends with the image. */
export const zSnapshotInput = z.object({
    url: zLinkUrl,
    viewport: zViewport.optional(),
    capturedBy: z.enum(CAPTURED_BY).default('upload'),
    elements: z.array(zElement).max(UI_LIMITS.elements).optional(),
});
export const zMarkInput = z.object({
    geometry: zMarkGeometry,
    note: z.string().max(UI_LIMITS.noteChars).default(''),
    /** An index into the snapshot's elements, when the mark was put on one. */
    element: z.number().int().min(0).max(UI_LIMITS.elements).optional(),
});
export const zMarkPatch = z
    .object({
    geometry: zMarkGeometry.optional(),
    note: z.string().max(UI_LIMITS.noteChars).optional(),
})
    .refine((p) => p.geometry !== undefined || p.note !== undefined, { message: 'nothing to change' });
export const REQUEST_STATUSES = ['draft', 'interpreting', 'interpreted', 'exported', 'applying', 'done'];
/** One mark's instruction (docs/ui-tab.md §7): numbered as the engineer sees it — snapshot, then mark. */
export const zInterpretationItem = z.object({
    snapshot: z.number().int().min(1),
    mark: z.number().int().min(1),
    understood: z.string().trim().min(1).max(1200),
    where: z.array(z.object({ file: z.string().min(1).max(500), line: z.number().int().min(1).optional() })).max(6),
    change: z.string().trim().max(1200),
    question: z.string().trim().max(1200).optional(),
});
// ---------------------------------------------------------------------------------
// The Chrome extension (docs/ui-tab.md §9.2): what Civil's page and the extension say.
// ---------------------------------------------------------------------------------
/**
 * The extension's id, fixed by the public key in its manifest (apps/extension), so
 * Civil's page talks to exactly this extension and no other.
 */
export const CIVIL_EXTENSION_ID = 'mbjnpgpbbghdpihoafpjflhkbkjkfcfa';
//# sourceMappingURL=ui.js.map